import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Film, ClipboardCheck, HelpCircle, Clock, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { FadeUp } from './MotionReveals';

const A_VENIR = [
  'Intersections', 'Feux tricolores', 'Stop', 'Cédez-le-passage', 'Priorité à droite',
  'Carrefour à l\'indonésienne', 'Voies d\'insertion', 'Voies d\'accélération', 'Voies de décélération',
  'Voies rapides', 'Autoroute', 'Passage à niveau',
];

export default function NouveauteCoursSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10 sm:mt-16" data-testid="nouveaute-cours-section">
      <FadeUp>
        <div className="relative overflow-hidden rounded-2xl sm:rounded-[28px] border border-red-700/40 bg-gradient-to-br from-[#1c0a0d] via-[#120a0b] to-[#0b0b0d] p-5 sm:p-10">
          {/* halo décoratif */}
          <div className="absolute -top-28 -left-20 w-96 h-96 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-[1.25fr_1fr] gap-7 lg:gap-10 items-center">
            {/* Texte */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-[11px] font-black uppercase tracking-[0.18em] shadow-lg shadow-red-900/50">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                  </span>
                  Nouveauté
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec font-bold uppercase tracking-[0.18em]">
                  <Film size={12} /> Cours interactif · Analyse vidéo
                </span>
              </div>

              <h2 className="font-serif-editorial text-white text-[30px] sm:text-[42px] lg:text-[50px] leading-[1.02]">
                Le giratoire décodé<br />
                <span className="text-red-500">seconde par seconde.</span>
              </h2>
              <p className="text-white/65 text-[13.5px] sm:text-[15px] mt-4 max-w-xl leading-relaxed">
                2 vidéos réelles embarquées (vue conducteur, plaques floutées) analysées par un moniteur ECSR :
                clique un timecode, la vidéo saute au bon moment. Quiz, pièges PL + moto, et grille
                de révision pour savoir si tu es prêt.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  { icon: Clock, t: 'Timeline à timecodes' },
                  { icon: HelpCircle, t: 'Quiz 3 questions' },
                  { icon: ClipboardCheck, t: 'Grille seuil 8/11' },
                  { icon: ShieldCheck, t: 'Gratuit · sans compte' },
                ].map(({ icon: Icon, t }, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/75 text-[11.5px] font-semibold">
                    <Icon size={12} className="text-red-400" /> {t}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/cours/analyse-giratoire" data-testid="nouveaute-cours-cta"
                  className="h-12 px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] sm:text-[15px] font-bold inline-flex items-center gap-2 shadow-xl shadow-red-900/50 transition-all hover:scale-[1.02]">
                  <Play size={16} className="fill-current" /> Découvrir le cours <ArrowRight size={15} />
                </Link>
                <span className="text-white/40 text-[11.5px] font-mono-spec">2 vidéos · 12 timecodes · 6 QCM · 22 critères</span>
              </div>
            </div>

            {/* Visuel vidéo */}
            <Link to="/cours/analyse-giratoire" data-testid="nouveaute-cours-visuel" className="relative block group">
              <div className="relative rounded-2xl overflow-hidden border border-white/15 shadow-2xl shadow-black/60 rotate-1 group-hover:rotate-0 transition-transform duration-300">
                <img src="/videos/giratoire/poster1.jpg" alt="Vue conducteur — analyse vidéo du giratoire" loading="lazy"
                  className="w-full aspect-[4/3] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-600/90 backdrop-blur flex items-center justify-center shadow-2xl shadow-red-900/60 group-hover:scale-110 transition-transform">
                    <Play size={28} className="text-white fill-current ml-1" />
                  </span>
                </div>
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-black/60 backdrop-blur border border-white/15 text-white text-[10.5px] font-mono-spec font-bold">
                  ● REC · Vue conducteur · 44 s
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="text-white text-[12px] font-bold drop-shadow">Giratoire hors agglo · PL + Moto</span>
                  <span className="px-2 py-0.5 rounded bg-red-600 text-white text-[9.5px] font-black uppercase tracking-wider">Plaques floutées</span>
                </div>
              </div>
            </Link>
          </div>

          {/* Teaser à venir */}
          <div className="relative mt-8 sm:mt-10 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-amber-300" />
              <span className="text-amber-300 text-[10.5px] font-mono-spec font-black uppercase tracking-[0.22em]">À venir très prochainement — même trame, nouvelles situations</span>
            </div>
            <div className="relative overflow-hidden" data-testid="nouveaute-teaser-marquee">
              <div className="flex gap-2 w-max animate-[marquee_28s_linear_infinite] hover:[animation-play-state:paused]">
                {[...A_VENIR, ...A_VENIR].map((t, i) => (
                  <span key={i} className="shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-[12px] font-semibold">
                    {t}
                    <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[8.5px] font-black uppercase tracking-wider">Bientôt</span>
                  </span>
                ))}
              </div>
              <div className="absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#120b0c] to-transparent pointer-events-none" />
              <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#0b0b0d] to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </FadeUp>
    </section>
  );
}
